import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import Logo from "../../asset/images/home/Logo (1).png";
import FastTrackLogo from "../../asset/images/home/Fast Track logo 1.png";

function Register() {
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        schoolDistrict: "",
        password: "",
        confirmPassword: "",
    });
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const { register } = useAuth();
    const navigate = useNavigate();

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
        setError("");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        if (formData.password !== formData.confirmPassword) {
            setError("Passwords do not match");
            return;
        }

        if (formData.password.length < 8) {
            setError("Password must be at least 8 characters long");
            return;
        }

        setLoading(true);

        try {
            const result = await register({
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                schoolDistrict: formData.schoolDistrict || undefined,
                password: formData.password,
            });

            if (result && result.success) {
                navigate("/register/verify-email", {
                    state: { email: formData.email },
                });
            } else {
                console.log('Registration failed, result:', result);
                const errorMessage = result?.error || "Registration failed. Please try again.";
                console.log('Setting error message:', errorMessage);
                setError(errorMessage);
            }
        } catch (err) {
            console.error('Registration error:', err);
            const errorMessage = err.response?.data?.error?.message 
                || err.response?.data?.message 
                || err.message 
                || "An unexpected error occurred. Please try again.";
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <header className="bg-white shadow-md">
                <div className="max-w-7xl mx-auto px-6 py-6">
                    <div className="flex justify-between items-center">
                        {/* Logo */}
                        <div className="flex items-center">
                            <img src={Logo} alt="READ 21 Logo" className="h-16 w-auto" />
                        </div>

                        <div className="flex items-center space-x-8">
                            <Link
                                to="/about"
                                className="bg-transparent text-gray-700 px-8 py-3 rounded-md font-medium text-lg hover:bg-primaryDark hover:text-white transition-colors"
                            >
                                About
                            </Link>

                            <Link to="/register">
                                <button className="bg-primary text-white px-8 py-3 rounded-md font-medium hover:bg-primaryDark transition-colors text-lg">
                                    Enroll Today
                                </button>
                            </Link>
                        </div>
                    </div>
                </div>
            </header>

            <section className="py-12">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="bg-white rounded-xl shadow-xl p-10">
                        {/* Main Grid */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                            {/* Left Image */}
                            <div className="flex justify-center">
                                <img src={FastTrackLogo} alt="READ 21 Logo" className="w-64 h-auto" />
                            </div>

                            {/* Right Side Form */}
                            <div className="space-y-5">
                                <div className="mb-12">
                                    <h1 className="text-3xl font-bold text-primary text-center mb-10">
                                        Register for Account
                                    </h1>

                                    {/* Steps */}
                                    <div className="relative flex items-center justify-between max-w-2xl mx-auto">
                                        <div className="absolute left-[7%] right-[7%] top-4 h-0.5 bg-gray-300 z-0"></div>

                                        <div className="flex flex-col items-center relative z-10">
                                            <div className="w-8 h-8 rounded-full bg-white border-2 border-primary flex items-center justify-center mb-2">
                                                <svg className="w-5 h-5 text-primary" viewBox="0 0 20 20" fill="currentColor">
                                                    <path
                                                        fillRule="evenodd"
                                                        clipRule="evenodd"
                                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                    />
                                                </svg>
                                            </div>
                                            <span className="text-sm text-primary">Registration</span>
                                        </div>

                                        <div className="flex flex-col items-center relative z-10">
                                            <div className="w-8 h-8 rounded-full bg-white border-2 border-gray-300 flex items-center justify-center mb-2"></div>
                                            <span className="text-sm text-primary">Payment</span>
                                        </div>

                                        <div className="flex flex-col items-center relative z-10">
                                            <div className="w-8 h-8 rounded-full bg-white border-2 border-gray-300 flex items-center justify-center mb-2"></div>
                                            <span className="text-sm text-primary">Complete</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Error Message */}
                                {error && (
                                    <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                                        <p className="text-sm text-red-600">{error}</p>
                                    </div>
                                )}

                                <form onSubmit={handleSubmit}>
                                    {/* First Name + Last Name */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <InputWithIcon
                                            name="firstName"
                                            placeholder="Enter First Name"
                                            value={formData.firstName}
                                            onChange={handleChange}
                                            required
                                        />
                                        <InputWithIcon
                                            name="lastName"
                                            placeholder="Enter Last Name"
                                            value={formData.lastName}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>

                                    {/* Email + School District */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <InputWithIcon
                                            name="email"
                                            type="email"
                                            placeholder="Enter Email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            required
                                        />
                                        <InputWithIcon
                                            name="schoolDistrict"
                                            placeholder="Enter School District"
                                            value={formData.schoolDistrict}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    {/* Password + Re-enter Password */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <PasswordInput
                                            name="password"
                                            placeholder="Enter Password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            required
                                        />
                                        <PasswordInput
                                            name="confirmPassword"
                                            placeholder="Re-enter Password"
                                            value={formData.confirmPassword}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>

                                    {/* Continue Button */}
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="w-full bg-primary hover:bg-primaryDark text-white font-semibold py-4 rounded-md transition-colors text-lg mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {loading ? "Registering..." : "Continue"}
                                    </button>
                                </form>

                                {/* Login Link */}
                                <p className="text-center text-sm text-gray-600 mt-4">
                                    Already have an account?{" "}
                                    <Link to="/login" className="text-primary hover:underline font-medium">
                                        Log in now.
                                    </Link>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-primary py-10">
                <div className="max-w-7xl mx-auto px-6 text-center">
                    <div className="mb-3">
                        <img src={Logo} alt="READ 21 Logo" className="mx-auto h-16 w-auto" />
                    </div>
                    <p className="text-white text-lg">Read 21 © 2025 - All Rights Reserved</p>
                </div>
            </footer>
        </>
    );
}

function InputWithIcon({ name, placeholder, type = "text", value, onChange, required }) {
    return (
        <div className="relative">
            <span className="absolute left-3 top-3 text-gray-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                </svg>
            </span>
            <input
                type={type}
                name={name}
                placeholder={placeholder}
                value={value}
                onChange={onChange}
                required={required}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
            />
        </div>
    );
}

function PasswordInput({ name, placeholder, value, onChange, required }) {
    return (
        <div className="relative">
            <span className="absolute left-3 top-3 text-gray-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                </svg>
            </span>
            <input
                type="password"
                name={name}
                placeholder={placeholder}
                value={value}
                onChange={onChange}
                required={required}
                className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
            />
        </div>
    );
}

export default Register;
